import Vue from 'vue'
import { addNamespace } from './utils'
import { isEmptyObject } from '@/utils/object'
import { getAllTopics } from '@/apis/topics'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{topics: Object<id, Object>}} TopicsStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const TopicsStoreTypes = {
  getters: {
    GET_ALL_TOPICS: 'getAllTopics',
    GET_TOPIC: 'getTopic',
  },
  actions: {
    FETCH_ALL: 'fetchAll',
  },
  mutations: {
    SET_TOPICS: 'setTopics',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const TopicsStoreNamespacedTypes = addNamespace(
  'topics',
  TopicsStoreTypes
)

/**
 * @returns {TopicsStoreState}
 */
export function state() {
  return {
    topics: {},
  }
}

export const getters = {
  /**
   * @param {TopicsStoreState} state
   * @returns {function():Array}
   */
  [TopicsStoreTypes.getters.GET_ALL_TOPICS]: state => () => {
    return isEmptyObject(state.topics) ? [] : Object.values(state.topics)
  },

  /**
   * @param {TopicsStoreState} state
   * @param {number} id
   * @returns {function(number):Object}
   */
  [TopicsStoreTypes.getters.GET_TOPIC]: state => id => {
    return state.topics[id] || null
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {TopicsStoreState} state
   * @returns {Promise}
   */
  [TopicsStoreTypes.actions.FETCH_ALL]({ commit, state }) {
    if (!isEmptyObject(state.topics)) {
      return Promise.resolve()
    }

    return getAllTopics().then(topics => {
      const map = {}

      topics.forEach(topic => {
        map[topic.id] = topic
      })

      commit(TopicsStoreTypes.mutations.SET_TOPICS, map)
    })
  },
}

export const mutations = {
  /**
   * @param {TopicsStoreState} state
   * @param {Object} topics
   */
  [TopicsStoreTypes.mutations.SET_TOPICS](state, topics) {
    Vue.set(state, 'topics', topics)
  },
}

export default {
  namespaced: true,
  Types: TopicsStoreTypes,
  NamespacedTypes: TopicsStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
